import { useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { getPermissionValueAtPath } from "../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import type { SubjectType } from "../types/esign.types.ts"

export const SEND_FOR_SIGNATURE_PERMISSION =
  "esign.signatures.document_send_for_signature" as const
export const CREATE_REQUEST_PERMISSION =
  "esign.requests.esign_request_create" as const

/**
 * Whether `codes` holds an EZ-Sign right split off Send for Signature. A
 * backend that predates the split answers with the Send right, as it always did.
 */
export const esignRight = (codes: unknown, path: string): boolean => {
  const sections = codes as { esign?: { requests?: unknown } } | undefined
  if (sections?.esign?.requests === undefined) {
    return getPermissionValueAtPath(codes, SEND_FOR_SIGNATURE_PERMISSION)
  }
  return getPermissionValueAtPath(codes, path)
}

export interface SignatureSubject {
  type: SubjectType
  id: string
}

export type OpenSignatureWizard = (subject?: SignatureSubject) => void

/** Opens the wizard on the record, or `null` for someone who may not raise a request. */
export const useSendForSignature = (
  leadId: string | null | undefined
): OpenSignatureWizard | null => {
  const navigate = useNavigate()
  const location = useLocation()
  const allowed = useAppSelector((state) =>
    esignRight(state.auth?.permissions?.codes, CREATE_REQUEST_PERMISSION)
  )

  return useMemo(
    () =>
      allowed && leadId
        ? (subject) =>
            navigate(
              RoutesPaths.esign.newEnvelope({
                lead: leadId,
                subject_type: subject?.type,
                subject_id: subject?.id
              }),
              // Closing the wizard comes back to this record, not to EZ-Sign.
              { state: { returnTo: `${location.pathname}${location.search}` } }
            )
        : null,
    [allowed, leadId, navigate, location.pathname, location.search]
  )
}
